import type { QueryClient } from '@tanstack/react-query';
import { companionsKeys } from './companions';
import { walletKeys } from './wallet';
import { journalKeys } from './journal';
import { questsKeys } from './quests';
import { worldKeys } from './world';

/**
 * QA 2026-09-23 bug 1 (the invisible async level-up). Every reward except a battle's — dailies,
 * quest steps, weeklies, freebies — is paid by the server's outbox, which `OutboxDispatcher`
 * sweeps every 500 ms, so the XP and Shells land up to ~1.5 s AFTER the triggering mutation's own
 * response. A mutation's `onSettled` invalidation therefore refetches too early, sees the old
 * level and the old balance, and nothing ever looks again: the level ring, the Shells chip and
 * the level-up celebration all missed every daily-task level-up in the game.
 *
 * This is the one place that knows about that lag. Call it from any mutation whose action can
 * complete a daily or a quest step; it re-invalidates the five queries a reward moves —
 * companions (level, xp, freePoints), wallet (Shells), the journal (the daily's tick), quests
 * (the step advanced — the Habitat's Next step card reads it) and the world snapshot (final
 * review I2: the same step advance also moves `GET /world`'s per-viewer `quest_step`/`plays_left`
 * status a moment after this fires) — a bounded number of times, then
 * stops. Not a poll: two timers, both cleared by nothing because an
 * invalidation on a query with no observer is a no-op, so an unmount mid-wait costs nothing.
 *
 * `delays` is a parameter only so a test can collapse it to `[0]`; callers take the default.
 */
export const REWARD_SETTLE_DELAYS_MS: readonly number[] = [1500, 4000];

export function scheduleRewardRefetch(queryClient: QueryClient, delays: readonly number[] = REWARD_SETTLE_DELAYS_MS) {
  for (const ms of delays) {
    setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: companionsKeys.all });
      void queryClient.invalidateQueries({ queryKey: walletKeys.all });
      void queryClient.invalidateQueries({ queryKey: journalKeys.all });
      void queryClient.invalidateQueries({ queryKey: questsKeys.all });
      void queryClient.invalidateQueries({ queryKey: worldKeys.all });
    }, ms);
  }
}
